import { createContainer } from 'unstated-next';
import { User, UserState } from 'core/types/user';
import { Media } from 'core/types/media';
import { useReducer } from 'react';
import useSnackbar from 'components/snackbar';
import p from 'translation';
import { updateProfilePicture, updateProfile } from './query';
import { UpdateProfile } from './types';

export * from './types';

interface Action {
  type: 'fetched' | 'updateProfilePicture';
}

interface Fetched extends Action {
  type: 'fetched';
  data: User;
}

interface UpdateProfilePicture extends Action {
  type: 'updateProfilePicture';
  data: Media;
}

type Actions = Fetched | UpdateProfilePicture;

function reducer(state: User, action: Actions): User {
  switch (action.type) {
    case 'fetched':
      return action.data;
    case 'updateProfilePicture':
      return {
        ...state,
        profilePicture: action.data,
      };
    default:
      return state;
  }
}

interface Dispatch {
  fetched: (profile: User) => void;
  updateProfile: (profile: UpdateProfile) => Promise<boolean>;
  updateProfilePicture: (image: Blob) => Promise<boolean>;
}

const emptyUser = (): User => ({
  id: '',
  language: '',
  role: {
    id: 'none',
    category: 'ORGANIZATION',
  },
  state: UserState.INVITED,
});

function useProfileReducer(initialState?: User): [User, Dispatch] {
  const { queue } = useSnackbar();
  const [state, dispatch] = useReducer(reducer, initialState || emptyUser());

  const dispatcher: Dispatch = {
    fetched: (profile: User) => {
      dispatch({ type: 'fetched', data: profile });
    },
    updateProfilePicture: async (image: Blob) => {
      const data = await updateProfilePicture(image);
      if (!data) {
        queue({
          message: p.t('apps.manage.snackbar.updated-profile-picture-error'),
        });
        return false;
      }
      queue({ message: p.t('apps.manage.snackbar.updated-profile-picture') });
      dispatch({ type: 'updateProfilePicture', data });
      return true;
    },
    updateProfile: async (profile: UpdateProfile) => {
      const data = await updateProfile(profile);
      if (!data) {
        queue({
          message: p.t('apps.manage.snackbar.updated-my-profile-error'),
        });
        return false;
      }
      queue({ message: p.t('apps.manage.snackbar.updated-my-profile') });
      dispatch({ type: 'fetched', data });
      return true;
    },
  };

  return [state, dispatcher];
}

const container = createContainer<[User, Dispatch], User>(useProfileReducer);
const useProfile = container.useContainer;
export const ProfileProvider = container.Provider;
export default useProfile;
